import OpenAI from 'openai';
import { AI_CONFIG, MODEL_CONFIGS } from '../../../config/ai.config';

const openai = new OpenAI({
  apiKey: AI_CONFIG.openai.apiKey,
  dangerouslyAllowBrowser: true, // Note: In production, proxy through backend
});

export interface ComplianceCheck {
  section: string;
  rules: Array<{
    id: string;
    description: string;
    status: 'passed' | 'warning' | 'failed';
    message: string;
    severity: 'high' | 'medium' | 'low';
    suggestions: string[];
  }>;
  score: number;
  requiredActions: string[];
}

export interface ComplianceReport {
  checks: ComplianceCheck[];
  overallScore: number;
  criticalIssues: number;
  warnings: number;
  suggestions: string[];
  lastUpdated: string;
}

export class ComplianceChecker {
  async checkCompliance(
    content: string,
    requirements: string[]
  ): Promise<ComplianceReport> {
    try {
      const prompt = this.buildCompliancePrompt(content, requirements);
      const response = await openai.chat.completions.create({
        ...MODEL_CONFIGS.compliance,
        messages: [{ role: 'user', content: prompt }],
      });

      return this.parseComplianceResponse(response.choices[0]?.message?.content || '');
    } catch (error) {
      console.error('Failed to check compliance:', error);
      throw error;
    }
  }

  private buildCompliancePrompt(content: string, requirements: string[]): string {
    return `Check the following content for compliance with government contracting requirements:

Content:
${content}

Requirements:
${JSON.stringify(requirements, null, 2)}

Please provide a detailed compliance analysis including:
1. Section-by-section compliance checks
2. Overall compliance score
3. Critical issues and warnings
4. Suggested improvements

Format the response as JSON.`;
  }

  private parseComplianceResponse(response: string): ComplianceReport {
    try {
      const parsed = JSON.parse(response);
      return {
        ...parsed,
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to parse compliance response:', error);
      throw error;
    }
  }

  async monitorComplianceUpdates(): Promise<void> {
    // In a real implementation, this would poll for regulatory updates
    console.log('Monitoring compliance updates...');
  }
}

export const complianceChecker = new ComplianceChecker();